import React, { useEffect, useState, useMemo } from 'react';
import {
  List,
  Tag,
  Button,
  Tooltip,
  Empty,
  Skeleton,
  Alert,
  Tabs,
} from 'antd';
import {
  FilePdfOutlined,
  CalendarOutlined,
  MedicineBoxOutlined,
  UserOutlined,
  FileTextOutlined,
  FormOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { getAccessTokenApi } from '../../api/auth';
import { getPortalPrescriptionsApi } from '../../api/prescription';
import '../../scss/portal-list.scss';
import './Prescriptions.scss';

function getMedicineLabel(prescription) {
  if (prescription.medicine) return prescription.medicine;
  if (prescription.medicines?.length > 0) {
    return prescription.medicines.map((m) => m.name).join(', ');
  }
  return 'Sin detalle de medicación';
}

function getDoctorName(prescription) {
  const { user } = prescription;
  if (!user) return null;
  return `${user.title || ''} ${user.name || ''} ${user.surname || ''}`.trim();
}

function PrescriptionItem({ prescription }) {
  const isPrescription = prescription.type === 'prescription';

  return (
    <List.Item
      className="portal-list-item"
      actions={[
        prescription.url && (
          <Tooltip title="Ver PDF">
            <Button
              type="link"
              className="portal-list-action-btn portal-list-action-btn--pdf"
              icon={<FilePdfOutlined />}
              onClick={() => window.open(prescription.url, '_blank')}
            />
          </Tooltip>
        ),
      ].filter(Boolean)}
    >
      <List.Item.Meta
        avatar={
          <div
            className={`portal-list-avatar ${
              isPrescription
                ? 'portal-list-avatar--prescription'
                : 'portal-list-avatar--order'
            }`}
          >
            <MedicineBoxOutlined />
          </div>
        }
        title={
          <div className="portal-list-title-row">
            <span className="portal-list-title">
              {getMedicineLabel(prescription)}
            </span>
            {prescription.type && (
              <Tag color={isPrescription ? 'green' : 'blue'}>
                {isPrescription ? 'RECETA' : 'ORDEN'}
              </Tag>
            )}
          </div>
        }
        description={
          <div className="portal-list-meta">
            {getDoctorName(prescription) && (
              <span className="portal-list-meta__item">
                <UserOutlined />
                {getDoctorName(prescription)}
              </span>
            )}
            {prescription.date && (
              <span className="portal-list-meta__item">
                <CalendarOutlined />
                {dayjs(prescription.date).format('DD MMM YYYY')}
              </span>
            )}
            {prescription.diagnosis && (
              <span className="portal-list-meta__secondary">
                {prescription.diagnosis}
              </span>
            )}
          </div>
        }
      />
    </List.Item>
  );
}

const FILTER_TYPES = [
  { key: 'all', label: 'Todos', icon: <FileTextOutlined /> },
  { key: 'prescription', label: 'Recetas', icon: <MedicineBoxOutlined /> },
  { key: 'order', label: 'Órdenes', icon: <FormOutlined /> },
];

export default function Prescriptions() {
  const [prescriptions, setPrescriptions] = useState([]);
  const [typeFilter, setTypeFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [recetarioEnabled, setRecetarioEnabled] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadPrescriptions = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = await getAccessTokenApi();
        if (!token) return;

        const response = await getPortalPrescriptionsApi({
          token,
          page: 1,
          limit: 20,
        });

        if (response?.code !== 200) {
          setError(response?.message || 'Error al cargar las prescripciones');
          setPrescriptions([]);
          return;
        }

        const data = response.data || [];
        const sorted = [...data].sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        );

        setRecetarioEnabled(response.recetarioEnabled !== false);
        setPrescriptions(sorted);
      } catch (err) {
        setError('Error al cargar las prescripciones');
      } finally {
        setLoading(false);
      }
    };

    loadPrescriptions();
  }, []);

  const getPrescriptionCount = (type) => {
    if (type === 'all') return prescriptions.length;
    return prescriptions.filter((item) => item.type === type).length;
  };

  const tabItems = useMemo(() => {
    const items = FILTER_TYPES.filter(
      (type) => type.key === 'all' || getPrescriptionCount(type.key) > 0
    ).map((type) => ({
      key: type.key,
      label: (
        <span className="tab-label">
          <span>{type.label}</span>
          {getPrescriptionCount(type.key) > 0 && (
            <span className="count-badge">{getPrescriptionCount(type.key)}</span>
          )}
        </span>
      ),
      icon: type.icon,
    }));

    return items;
  }, [prescriptions]);

  const filteredPrescriptions = useMemo(() => {
    if (typeFilter === 'all') return prescriptions;
    return prescriptions.filter((item) => item.type === typeFilter);
  }, [prescriptions, typeFilter]);

  if (loading) {
    return <Skeleton active paragraph={{ rows: 4 }} />;
  }

  if (!recetarioEnabled) {
    return (
      <Alert
        type="info"
        showIcon
        message="Recetario no disponible"
        description="Este centro aún no tiene habilitada la visualización de recetas en el portal."
      />
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="No se pudieron cargar las prescripciones"
        description={error}
      />
    );
  }

  if (prescriptions.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="No tenés recetas ni órdenes disponibles"
      />
    );
  }

  return (
    <div className="prescriptions-panel">
      <div className="portal-list-toolbar">
        <div className="portal-list-filters">
          <Tabs
            activeKey={typeFilter}
            onChange={setTypeFilter}
            items={tabItems}
            size="small"
            className="portal-type-tabs"
          />
        </div>
      </div>

      {filteredPrescriptions.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            typeFilter === 'prescription'
              ? 'No tenés recetas disponibles'
              : 'No tenés órdenes disponibles'
          }
        />
      ) : (
        <List
          className="prescriptions-list"
          itemLayout="horizontal"
          dataSource={filteredPrescriptions}
          renderItem={(prescription) => (
            <PrescriptionItem
              key={prescription.id}
              prescription={prescription}
            />
          )}
        />
      )}
    </div>
  );
}
